import Settings from "./settings";

/**
 *  API class that interacts with the DataTools Kleber API
 */
class API {

	/**
	 * Fetch the access key and department name for later use in API requests
	 */
	init() {
		if (!this.accessKey) {
			this.accessKey = this.getAccessKey();

			setInterval(function () {
				this.accessKey = this.getAccessKey();
			}.bind(this), 4.5 * 60 * 1000);
		}

		this.departmentName = Settings.getDepartmentName();
	}

	/**
	 * Get a temporary access key for usage with the DataTools API
	 *
	 * @returns {Promise<string>}
	 */
	getAccessKey() {
		return this.requestWP('datatools/v1/get_key');
	}

	/**
	 * Get the Authentication GUID.
	 *
	 * The GUID provided when you registered with ABR for access to the web services.
	 * Visit http://abr.business.gov.au/RegisterAgreement.aspx to register for a GUID.
	 *
	 * @returns {Promise<string>}
	 */
	getAbrAuthenticationGuid() {
		return this.requestWP('datatools/v1/get_abr_authentication_guid');
	}

	setAbrAuthenticationGuid() {
		// Already set
		if (this.abrAuthenticationGuid) {
			return;
		}

		this.getAbrAuthenticationGuid().then((authenticationGuid) => {
			this.abrAuthenticationGuid = authenticationGuid;
		});
	}

	/**
	 * API call to the address prediction service to get a list of matched addresses
	 *
	 * @param {string} term
	 * @returns {Promise<Object>}
	 */
	searchAddress(term) {
		return this.request({
			ResultLimit: 10,
			AddressLine: term,
			Method: "DataTools.Capture.Address.Predictive.AuPaf.SearchAddress",
		});
	}

	/**
	 * API call to get detailed address information for a specific record
	 *
	 * @param {string} recordId
	 * @returns {Promise<Object>}
	 */
	retrieveAddress(recordId) {
		return this.request({
			RecordId: recordId,
			Method: "DataTools.Capture.Address.Predictive.AuPaf.RetrieveAddress",
		});
	}

	/**
	 * API call to get business information for a given business name
	 *
	 * @param {string} businessName
	 * @returns {Promise<Object>}
	 */
	retrieveBusinessInfoByBusinessName( businessName ) {
		return this.request({
			AuthenticationGuid: this.abrAuthenticationGuid,
			BusinessName: businessName,
			Method: "DataTools.Capture.BusinessName.Search.AuAbr.SearchBusinessName",
		});
	}

	/**
	 * API call to verify if an email address is valid.
	 *
	 * @param {string} email
	 * @returns {Promise<Object>}
	 */
	verifyEmail( email ) {
		return this.request({
			EmailAddress: email,
			Method: "DataTools.Verify.Email.BriteVerify.VerifyEmail",
		});
	}

	/**
	 * Format the address into a standard format
	 *
	 * @param {Object} data
	 * @returns {{AddressLine2: string, AddressLine1: string, State: string, City: string, Postcode: string}}
	 */
	formatAddress(data) {
		let address = {
			AddressLine1: "",
			AddressLine2: "",
			City: "",
			State: "",
			Postcode: "",
		};

		if (!data.hasOwnProperty('DtResponse')
			|| !data.DtResponse.hasOwnProperty('Result')
			|| data.DtResponse.Result.length === 0
		) {
			return address;
		}

		let result = data.DtResponse.Result[0];
		let addressLines = this.getAddressLines(result);

		if (addressLines.length > 0) {
			address.AddressLine1 = addressLines[0];

			if (addressLines.length === 2) {
				address.AddressLine2 = addressLines[1];
			}
		}

		address.City = result.Locality;
		address.State = result.State;
		address.Postcode = result.Postcode;

		return address;
	}

	/**
	 * Parse the address into multiple lines
	 *
	 * @param {Object} result
	 * @returns {Array}
	 */
	getAddressLines(result) {
		let addressLines = [];

		let line1Parts = [];
		let line2Parts = [];

		//ADDRESS LINE 1

		//Building name
		if (result.hasOwnProperty("BuildingName") && result.BuildingName.length > 0) {
			line1Parts.push(result.BuildingName);
		}

		//Unit type
		if (result.hasOwnProperty("UnitType") && result.UnitType.length > 0) {
			line1Parts.push(result.UnitType);
		}

		//Unit number
		if (result.hasOwnProperty("UnitNumber") && result.UnitNumber.length > 0) {
			line1Parts.push(result.UnitNumber);
		}

		//Level type
		if (result.hasOwnProperty("LevelType") && result.LevelType.length > 0) {
			line1Parts.push(result.LevelType);
		}

		//Level number
		if (result.hasOwnProperty("LevelNumber") && result.LevelNumber.length > 0) {
			line1Parts.push(result.LevelNumber);
		}

		//ADDRESS LINE 2

		//PO Boxes
		if (result.hasOwnProperty("PostBoxNumber") && result.PostBoxNumber.length > 0) {
			line2Parts.push("PO Box");

			let POBoxPrefix = result.hasOwnProperty("PostBoxNumberPrefix") && result.PostBoxNumberPrefix.length > 0 ? result.PostBoxNumberPrefix : "";
			let POBoxSuffix = result.hasOwnProperty("PostBoxNumberSuffix") && result.PostBoxNumberSuffix.length > 0 ? result.PostBoxNumberSuffix : "";

			line2Parts.push(POBoxPrefix + result.PostBoxNumber + POBoxSuffix);
		} else {
			//Lot
			if (result.hasOwnProperty("LotNumber") && result.LotNumber.length > 0) {
				line2Parts.push("Lot " + result.LotNumber);
			}

			//Street Number
			if (result.hasOwnProperty("StreetNumber1") && result.StreetNumber1.length > 0) {
				let streetNumberSuffix1 = result.hasOwnProperty("StreetNumberSuffix1") && result.StreetNumberSuffix1.length > 0 ? result.StreetNumberSuffix1 : "";
				let streetNumber2 = "";

				if (result.hasOwnProperty("StreetNumber2") && result.StreetNumber2.length > 0) {
					let streetNumberSuffix2 = result.hasOwnProperty("StreetNumberSuffix2") && result.StreetNumberSuffix2.length > 0 ? result.StreetNumberSuffix2 : "";
					streetNumber2 = '-' + result.StreetNumber2 + streetNumberSuffix2;
				}

				line2Parts.push(result.StreetNumber1 + streetNumberSuffix1 + streetNumber2);
			}

			//Street Name
			if (result.hasOwnProperty("StreetName") && result.StreetName.length > 0) {
				line2Parts.push(result.StreetName);
			}

			//Street Type
			if (result.hasOwnProperty("StreetType") && result.StreetType.length > 0) {
				line2Parts.push(result.StreetType);
			}

			//Street Suffix
			if (result.hasOwnProperty("StreetSuffix") && result.StreetSuffix.length > 0) {
				line2Parts.push(result.StreetSuffix);
			}
		}

		if (line1Parts.length > 0) {
			addressLines.push(line1Parts.join(" "));
		}

		if (line2Parts.length > 0) {
			addressLines.push(line2Parts.join(" "));
		}

		return addressLines;
	}

	/**
	 * Make an API request to the Kleber API
	 *
	 * @param {Object} params
	 * @returns {Promise<Object>}
	 */
	request(params) {
		return new Promise((resolve, reject) => {
			Promise.all([this.accessKey, this.departmentName]).then((config => {
				let requestKey = config[0];
				let departmentCode = config[1];

				let ajaxRequest = new XMLHttpRequest();
				let url = "https://Kleber.datatoolscloud.net.au/KleberWebService/DtKleberService.svc/ProcessQueryStringRequest";
				let urlObject = new URL(url);

				urlObject.searchParams.set("OutputFormat", "json");
				urlObject.searchParams.set('RequestKey', requestKey);

				if (departmentCode.length > 0) {
					urlObject.searchParams.set('DepartmentCode', departmentCode);
				}

				Object.keys(params).forEach(function (key) {
					urlObject.searchParams.set(key, params[key]);
				});

				ajaxRequest.onreadystatechange = function () {
					if (this.readyState === 4 && this.status === 200) {
						let data = JSON.parse(this.responseText);
						resolve(data);
					}
				};
				ajaxRequest.onerror = reject;
				ajaxRequest.open('GET', urlObject.toString());
				ajaxRequest.send();
			})).catch(() => reject);
		});
	}

	/**
	 * Make an API request to the WP Rest API
	 *
	 * @param {string} path - The path for the API request
	 * @returns {Promise<string>} - Returns a Promise with the response from the API
	 */
	async requestWP(path) {
		try {
			await wp.api.loadPromise;
			const newPath = `${path}?t=${Date.now()}`;

			return await wp.apiRequest({path: newPath});
		} catch (error) {
			throw new Error(error);
		}
	}

}

const APIInstance = new API();
export default APIInstance;
